import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FileText,
  PackageCheck,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Truck,
} from "lucide-react";
import {
  Fragment,
  type ReactNode,
} from "react";
import {
  AutocompleteSelect,
} from "../../../../shared/components/AutocompleteSelect";
import { CorrectionDossierPanel } from "../shared/CorrectionDossierPanel";
import { RequestDocumentsPanel } from "../shared/RequestDocuments";
import type {
  useOffersWorkspace,
} from "./useOffersWorkspace";


function OfferWorkspaceState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="offer-workspace-state" role="status">
      <span className="offer-workspace-state-icon" aria-hidden="true">
        <FileText size={20} />
      </span>
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      {action ? <div className="offer-workspace-state-action">{action}</div> : null}
    </div>
  );
}

export function OfferLedger({ model }: { model: ReturnType<typeof useOffersWorkspace>; }) {
  const {
    requestWorkspaceService,
    user,
    onOpenPurchaseOrder,
    query,
    setQuery,
    pendingOfferRemovalId,
    setPendingOfferRemovalId,
    evaluation,
    evaluationLoading,
    activeRequestNo,
    setActiveRequestNo,
    setDecisionEditorOpen,
    completedDecisionOpen,
    setCompletedDecisionOpen,
    purchaseOrderLink,
    documentsRefreshVersion,
    requestOptions,
    visibleRows,
    openOfferEditor,
    removeOffer,
    activeRequest,
    completedGeneralManagerDecision,
    completedManagerDecision,
    completedFinalOffer,
    completedSpecialistOffer,
    activeCorrection,
    approvalStarted,
    evaluationReady,
    canManageActiveOffers,
    excludedOffers,
    evaluationBlockerReasons,
    evaluationStatusLabel,
    activeRequestHasOffers,
    openNewOffer,
  } = model;
  return (<>
    <section className="module-card offers-ledger">
      <div className="module-toolbar offer-list-toolbar">
        <div className="module-search">
          <Search size={16} />
          <input
            placeholder="Teklif no veya tedarikçi ara"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <AutocompleteSelect
          className="offer-request-selector"
          label="Talep seçimi"
          value={activeRequestNo}
          options={requestOptions.map((request) => ({
            value: request.id,
            label: request.title,
            description: `${request.id} · ${request.department} · ${request.offers} teklif`,
            keywords: [request.title, request.department],
          }))}
          placeholder="Talep numarası veya ürün ara"
          emptyMessage="Teklif sürecine alınmış talep bulunmuyor."
          onChange={setActiveRequestNo}
        />
        {activeRequestNo ? (
          <div
            className={`evaluation-version ${evaluationLoading ? "is-loading" : ""
              } ${evaluation && !evaluationReady ? "is-blocked" : ""}`}
            role="status"
          >
            {evaluationLoading ? (
              <RotateCcw size={15} />
            ) : (
              <CheckCircle2 size={15} />
            )}
            <span>
              <small>Otomatik değerlendirme</small>
              <strong>
                {evaluationLoading
                  ? "Yenileniyor"
                  : !activeRequestHasOffers
                    ? "Teklif bekleniyor"
                    : evaluation
                      ? evaluationStatusLabel
                      : "Teklif bekleniyor"}
              </strong>
            </span>
          </div>
        ) : null}
      </div>
      {activeCorrection && !approvalStarted ? (
        <CorrectionDossierPanel
          task={activeCorrection}
          onReviewDecision={() => setDecisionEditorOpen(true)}
        />
      ) : null}
      {activeRequest?.status === "Onaylandı" ? (
        <div className="completed-offer-summary" role="status">
          <span className="completed-offer-summary-icon">
            <CheckCircle2 size={17} />
          </span>
          <div className="completed-offer-summary-copy">
            <small>Nihai karar tamamlandı</small>
            <strong>
              Nihai seçim:{" "}
              {completedFinalOffer?.supplier ??
                completedGeneralManagerDecision?.decidedOfferSupplier ??
                "Karar kaydı"}
            </strong>
            <span>
              {purchaseOrderLink.state === "existing"
                ? `${purchaseOrderLink.orderNo} oluşturuldu; teslimat sürecini izleyin.`
                : purchaseOrderLink.state === "candidate"
                  ? "Nihai seçim siparişe dönüştürülmeye hazır."
                  : purchaseOrderLink.state === "unavailable"
                    ? "Bu eski kayda bağlı sipariş adayı bulunamadı."
                    : "Nihai seçime bağlı sipariş durumu kontrol ediliyor."}
            </span>
          </div>
          <div className="completed-offer-summary-actions">
            {purchaseOrderLink.state === "candidate" ||
              purchaseOrderLink.state === "existing" ? (
              <button
                type="button"
                className="completed-offer-order-action"
                onClick={() => onOpenPurchaseOrder(activeRequestNo)}
              >
                {purchaseOrderLink.state === "candidate" ? (
                  <PackageCheck size={16} />
                ) : (
                  <Truck size={16} />
                )}
                {purchaseOrderLink.state === "candidate"
                  ? "Sipariş oluştur"
                  : "Siparişi görüntüle"}
              </button>
            ) : null}
            <button
              type="button"
              className="completed-offer-summary-action"
              aria-expanded={completedDecisionOpen}
              onClick={() => setCompletedDecisionOpen((current) => !current)}
            >
              {completedDecisionOpen
                ? "Karar geçmişini kapat"
                : "Karar geçmişini göster"}
              <ChevronDown size={15} />
            </button>
          </div>
        </div>
      ) : null}
      {activeRequest?.status === "Onaylandı" && completedDecisionOpen ? (
        <section className="completed-offer-audit" aria-label="Karar geçmişi">
          {completedGeneralManagerDecision?.decisionKind ===
            "GM_FINAL_OVERRIDE" ? (
            <div className="completed-offer-audit-change">
              <strong>Nihai seçim Genel Müdür tarafından değiştirildi</strong>
              <span>
                {completedManagerDecision?.decidedOfferSupplier ??
                  completedSpecialistOffer?.supplier ??
                  "Önceki seçim kaydı yok"}{" "}
                yerine{" "}
                {completedGeneralManagerDecision.decidedOfferSupplier ??
                  completedFinalOffer?.supplier ??
                  "nihai teklif"}{" "}
                seçildi.
              </span>
              <p>
                {completedGeneralManagerDecision.decisionNote ??
                  "Değişiklik gerekçesi eski kayıtta bulunmuyor."}
              </p>
            </div>
          ) : null}
          <div className="completed-offer-audit-steps">
            <article>
              <span>1</span>
              <div>
                <small>Satın Alma Uzmanı önerisi</small>
                <strong>
                  {completedSpecialistOffer?.supplier ??
                    "Uzman önerisi eski kayıtta bulunmuyor"}
                </strong>
                <p>
                  {evaluation?.recommendation?.createdByName ??
                    "Kayıtlı uzman yok"}
                  {evaluation?.recommendation?.createdAt
                    ? " · " +
                    new Date(
                      evaluation.recommendation.createdAt,
                    ).toLocaleString("tr-TR")
                    : ""}
                </p>
              </div>
            </article>
            <article>
              <span>2</span>
              <div>
                <small>Satın Alma Müdürü kararı</small>
                <strong>
                  {completedManagerDecision?.decidedOfferSupplier ??
                    completedSpecialistOffer?.supplier ??
                    "Onay kaydı"}
                </strong>
                <p>
                  {completedManagerDecision?.decidedBy ??
                    "Kayıtlı karar sahibi yok"}
                  {completedManagerDecision?.decidedAt
                    ? " · " +
                    new Date(
                      completedManagerDecision.decidedAt,
                    ).toLocaleString("tr-TR")
                    : ""}
                </p>
              </div>
            </article>
            <article>
              <span>3</span>
              <div>
                <small>Genel Müdür nihai kararı</small>
                <strong>
                  {completedGeneralManagerDecision?.decidedOfferSupplier ??
                    completedFinalOffer?.supplier ??
                    "Nihai onay kaydı"}
                </strong>
                <p>
                  {completedGeneralManagerDecision?.decidedBy ??
                    "Kayıtlı karar sahibi yok"}
                  {completedGeneralManagerDecision?.decidedAt
                    ? " · " +
                    new Date(
                      completedGeneralManagerDecision.decidedAt,
                    ).toLocaleString("tr-TR")
                    : ""}
                </p>
              </div>
            </article>
          </div>
        </section>
      ) : null}
      {user.role === "SATIN_ALMA" &&
        activeRequestHasOffers &&
        evaluation &&
        !evaluationReady &&
        !approvalStarted ? (
        <div className="offer-evaluation-blocker" role="status">
          <span className="offer-evaluation-blocker-icon" aria-hidden="true">
            <AlertTriangle size={19} />
          </span>
          <div className="offer-evaluation-blocker-copy">
            <div className="offer-evaluation-blocker-heading">
              <strong>Karar için uygun teklif bulunamadı</strong>
              {excludedOffers.length ? (
                <span>
                  {excludedOffers.length} teklif karşılaştırma dışında
                </span>
              ) : null}
            </div>
            <p>{evaluation.message}</p>
            {evaluationBlockerReasons.length ? (
              <ul className="offer-evaluation-blocker-reasons">
                {evaluationBlockerReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
      {!requestOptions.length ? (
        <OfferWorkspaceState
          title="Teklif sürecinde talep bulunmuyor"
          description="Planlamadan Satın Almaya iletilen talepler burada görünür. Yeni bir talep geldiğinde teklif kaydına başlayabilirsiniz."
        />
      ) : !activeRequestNo ? (
        <OfferWorkspaceState
          title="Çalışacağınız talebi seçin"
          description="Teklifleri görüntülemek veya yeni teklif kaydetmek için üstteki talep alanından bir kayıt seçin."
        />
      ) : !activeRequestHasOffers ? (
        <OfferWorkspaceState
          title="Bu talep için henüz teklif yok"
          description={`${activeRequest?.title ?? activeRequestNo} için ilk tedarikçi teklifini kaydederek ticari karşılaştırmayı başlatın.`}
          action={
            canManageActiveOffers ? (
              <button
                type="button"
                className="primary-button"
                onClick={openNewOffer}
              >
                <Plus size={16} /> İlk teklifi ekle
              </button>
            ) : undefined
          }
        />
      ) : !visibleRows.length ? (
        <OfferWorkspaceState
          title="Aramayla eşleşen teklif yok"
          description="Teklif numarası veya tedarikçi aramasını değiştirin."
          action={
            <button
              type="button"
              className="secondary-button"
              onClick={() => setQuery("")}
            >
              Aramayı temizle
            </button>
          }
        />
      ) : (
        <div className="data-table-wrap">
          <table className="data-table offers-table">
            <thead>
              <tr>
                <th>Teklif</th>
                <th>Tedarikçi</th>
                <th>Birim / toplam</th>
                <th>Ödeme vadesi</th>
                <th>Teslimat</th>
                <th>Durum</th>
                {canManageActiveOffers ? <th>İşlemler</th> : null}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((offer) => (
                <Fragment key={offer.id}>
                  <tr>
                    <td>
                      <div className="offer-record-cell">
                        <span>
                          <FileText size={15} />
                        </span>
                        <div>
                          <strong>{offer.id}</strong>
                          <small>Teklif kaydı</small>
                        </div>
                      </div>
                    </td>
                    <td className="offer-supplier-cell">
                      <strong>{offer.supplier}</strong>
                    </td>
                    <td className="numeric offer-price-cell">
                      <strong>{offer.amount}</strong>
                      <small>
                        {offer.unitPrice === null
                          ? "Birim fiyat girilmemiş"
                          : `${offer.unitPrice.toLocaleString("tr-TR")} ${offer.currency} / ${offer.unit ?? "birim"}`}
                      </small>
                    </td>
                    <td>{offer.payment}</td>
                    <td>{offer.delivery}</td>
                    <td>
                      <span
                        className={`plain-status plain-${offer.status.toLowerCase().replaceAll(" ", "-")}`}
                      >
                        {offer.status}
                      </span>
                    </td>
                    {canManageActiveOffers ? (
                      <td>
                        <div className="offer-row-actions">
                          <button
                            type="button"
                            className="table-action"
                            aria-label={`${offer.id} teklifini düzenle`}
                            onClick={() => openOfferEditor(offer)}
                          >
                            <Pencil size={14} /> Düzenle
                          </button>
                          <button
                            type="button"
                            className="table-action is-danger"
                            aria-label={`${offer.id} teklifini kaldır`}
                            onClick={() => setPendingOfferRemovalId(offer.id)}
                          >
                            <Trash2 size={14} /> Kaldır
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                  {pendingOfferRemovalId === offer.id ? (
                    <tr className="offer-removal-confirmation">
                      <td colSpan={canManageActiveOffers ? 7 : 6}>
                        <div>
                          <span>
                            <strong>{offer.supplier}</strong> teklifini
                            kaldırmak istediğinize emin misiniz? Kayıt denetim
                            geçmişinde korunur.
                          </span>
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => setPendingOfferRemovalId(null)}
                          >
                            Vazgeç
                          </button>
                          <button
                            type="button"
                            className="reject-button"
                            onClick={() => removeOffer(offer)}
                          >
                            Teklifi kaldır
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {activeRequestNo ? (
        <div className="offer-documents-workspace">
          <RequestDocumentsPanel
            requestNo={activeRequestNo}
            service={requestWorkspaceService}
            refreshKey={documentsRefreshVersion}
          />
        </div>
      ) : null}
    </section>
  </>);
}
